import type {
  ActivityDefinition,
  CarePlan,
  Goal,
  PlanDefinition,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { useEffect, useMemo, useState } from 'react';

import {
  PlanShapeBadge,
  PlanSpecView,
  fetchPlanActivityDefinitions,
  fetchPlanDefinitionForCarePlan,
  loadCarePlanInstance,
} from '@eluve/care-plan-render';
import {
  Button,
  Heading,
  Icon,
  LoadingIcon,
  Text,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useMedplum } from '@eluve/medplum-client';
import { fhirToSpec, seedInstanceSpec } from '@eluve/plan-shapes';

import { assertBatchSucceeded, buildAcceptBundle } from './care-plan-instance';
import { carePlanSummary } from './care-plan-meta';

// The Care Plan tab for a NON-PHASED (continuous/episodic) draft (ENG-4773).
// A verbatim instance is the template bound to a patient — no per-patient
// divergence, so there's no tailoring chat: we render the template's own shape
// view read-only and offer a single Accept. The phased tab (CarePlanTab) owns
// the chat/tailor/diff path; this is its verbatim sibling, branched above in
// ActiveAppointmentLayout on `carePlanShapeOf`.
export const CarePlanReadOnlyView: React.FC<{
  carePlan: CarePlan;
  onBackToNote: () => void;
  onAccepted: () => void;
}> = ({ carePlan, onBackToNote, onAccepted }) => {
  const { t } = useTranslation();
  const { client } = useMedplum();
  const toast = useToast();

  const [template, setTemplate] = useState<PlanDefinition | null>(null);
  const [activityDefinitions, setActivityDefinitions] = useState<
    ActivityDefinition[]
  >([]);
  // The draft's existing children — the proposed goals (continuous control
  // bands included) Accept reconciles against. A verbatim draft has no
  // ServiceRequests, so that list stays empty.
  const [children, setChildren] = useState<{
    goals: Goal[];
    serviceRequests: ServiceRequest[];
  }>({ goals: [], serviceRequests: [] });
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  // Resolve the template by the canonical the draft instantiates, plus the
  // draft's existing children. The view renders the template directly (the
  // instance carries no per-patient divergence to render from).
  useEffect(() => {
    if (!client) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const pd = await fetchPlanDefinitionForCarePlan(client, carePlan);
        const ads = pd ? await fetchPlanActivityDefinitions(client, pd) : [];
        const { goals, serviceRequests } = await loadCarePlanInstance(
          client,
          carePlan,
        );
        if (!cancelled) {
          setTemplate(pd ?? null);
          setActivityDefinitions(ads);
          setChildren({ goals, serviceRequests });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, carePlan]);

  const spec = useMemo(
    () => (template ? fhirToSpec(template, { activityDefinitions }) : null),
    [template, activityDefinitions],
  );

  const summary = carePlanSummary(carePlan);
  const accepted = carePlan.status === 'active';

  const handleAccept = async () => {
    if (!client || !template) {
      return;
    }
    setAccepting(true);
    try {
      const startDate =
        carePlan.period?.start?.slice(0, 10) ??
        new Date().toISOString().slice(0, 10);
      // Re-seed the verbatim spec from the template and reuse the shared accept
      // path: it deletes the proposed children and re-mints them accepted while
      // preserving the CarePlan's id, identifier, encounter, and extensions.
      const finalSpec = seedInstanceSpec(
        template,
        { activityDefinitions },
        { startDate },
      );
      if (!finalSpec) {
        throw new Error('Could not seed care plan from its template');
      }
      const bundle = buildAcceptBundle({
        draftCarePlan: carePlan,
        existingGoals: children.goals,
        existingServiceRequests: children.serviceRequests,
        finalSpec,
      });
      const result = await client.executeBatch(bundle);
      assertBatchSucceeded(result);
      client.invalidateSearches('CarePlan');
      client.invalidateSearches('Goal');
      toast.success(t('carePlans.tab.carePlanAccepted'));
      onAccepted();
    } catch (err) {
      toast.error(
        t('carePlans.tab.acceptFailed', {
          error: err instanceof Error ? err.message : String(err),
        }),
      );
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-16rem)] min-h-96 flex-col">
      <div className="flex items-center justify-between gap-3 pb-3">
        <button
          type="button"
          onClick={onBackToNote}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <Icon name="ArrowLeft" size="auto" className="size-4" />
          {t('carePlans.tab.backToNote')}
        </button>
        {!accepted ? (
          <Button
            onClick={handleAccept}
            disabled={accepting || loading || !spec}
          >
            {accepting ? (
              <LoadingIcon />
            ) : (
              <Icon name="CheckCircle2" size="auto" />
            )}
            {t('carePlans.tab.acceptPlan')}
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-bg-positive-subtle px-3 py-1 text-sm font-medium text-text-positive">
            <Icon name="CheckCircle2" size="auto" className="size-4" />
            {t('carePlans.tab.accepted')}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="rounded-xl border border-border bg-card">
          <div className="px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <Text
                size="xs"
                weight="medium"
                className="tracking-wider text-muted-foreground uppercase"
              >
                {t('carePlans.tab.recommended')}
              </Text>
              {summary.matchPct !== null ? (
                <span className="inline-flex items-center rounded-full bg-bg-positive-subtle px-2 py-0.5 text-[11px] font-semibold text-text-positive">
                  {t('carePlans.tab.percentMatch', { pct: summary.matchPct })}
                </span>
              ) : null}
              <span className="ml-auto">
                <PlanShapeBadge shape={summary.shape} />
              </span>
            </div>
            <Heading size="lg" weight="semibold" level={2} className="mt-1.5">
              {summary.title}
            </Heading>
            <Text
              size="xs"
              color="secondary"
              className="mt-0.5 flex items-center gap-1.5"
            >
              <Icon name="Calendar" size="auto" className="size-3.5" />
              {t('carePlans.tab.startsOn', {
                date: carePlan.period?.start?.slice(0, 10) ?? '—',
              })}
            </Text>
          </div>

          <div className="border-t border-border px-4 py-3">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingIcon />
              </div>
            ) : !spec ? (
              <Text size="sm" color="secondary" className="py-6">
                {t('carePlans.tab.templateLoadFailed')}
              </Text>
            ) : (
              <div className="space-y-4">
                {summary.rationale.length > 0 ? (
                  <ul className="space-y-1">
                    {summary.rationale.map((r, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-foreground"
                      >
                        <Icon
                          name="Check"
                          size="auto"
                          className="mt-0.5 size-3.5 shrink-0 text-text-positive"
                        />
                        <span>
                          <span className="font-semibold">{r.label}</span>
                          {r.text ? ` — ${r.text}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <PlanSpecView spec={spec} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
